import React, {forwardRef, memo, useCallback, useRef} from 'react'
import {Stage, Layer} from 'react-konva'
import {Shape} from '../../../shapes/Shape'
import {SHAPE_TYPES} from '../../../../constant/shapes'
import clsx from '../../../../helpers/clsx'
import {calcScale} from '../../../../helpers/shapes/calcScale'
import useEditorSafeZone from '../../../../hooks/useEditorSafeZone'

const BASE_PROPS = {
    fill: '#303238',
    stroke: '#303238',
    strokeWidth: 0,
}

/**
 * Shape item component
 * @param {Object} param - Shape configuration
 * @param {import('./types').ShapeType} param.shapeType - Shape type
 * @param {string} param.name - Shape name
 * @param {string} param.description - Shape description
 * @param {string} [param.fill='#303238'] - Fill color
 * @param {string} [param.stroke='#303238'] - Stroke color
 * @param {number} [param.strokeWidth=0] - Stroke width
 * @param {number} [param.canvasSize=70] - Canvas size
 * @param {Object} [param.template] - Template object
 * @returns {JSX.Element} - Shape item component
 */
export const ShapeItem = memo(
    forwardRef(({className, shapeType, canvasSize = 70, template, name, description, onEdit, ...rest}, ref) => {
        const shapeRef = useRef(null)
        const isTemplateShape = shapeType === SHAPE_TYPES.TEMPLATE && template
        const canvasCenter = canvasSize / 2
        const props = isTemplateShape
            ? {
                  ...BASE_PROPS,
                  width: canvasSize,
                  height: canvasSize,
                  template,
                  shapeType,
              }
            : {
                  ...BASE_PROPS,
                  shapeType,
                  x: canvasCenter,
                  y: canvasCenter,
              }

        const {setSafeZone, background} = useEditorSafeZone()

        const drawSafeZone = useCallback(() => {
            const shapeNode = shapeRef.current
            if (!background) {
                return null
            }
            if (!shapeNode) {
                return {
                    src: background.src,
                    scaleX: 1,
                    scaleY: 1,
                }
            }
            const {width: bgWidth = 1000, height: bgHeight = 1000} = background
            // Clone shape
            const size = Math.min(bgWidth, bgHeight)
            if (isTemplateShape) {
                const scale = calcScale(
                    {
                        width: shapeNode.width(),
                        height: shapeNode.height(),
                    },
                    size,
                    true,
                )
                return {
                    src: shapeNode
                        .clone({
                            scaleX: 1,
                            scaleY: 1,
                        })
                        .toDataURL({
                            pixelRatio: 1,
                            mimeType: 'image/png',
                        }),
                    ...scale,
                }
            }
            const scale = calcScale(
                {
                    width: shapeNode.rawWidth(),
                    height: shapeNode.rawHeight(),
                },
                size,
                true,
            )
            return {
                src: shapeNode.clone({...scale}).toDataURL({
                    pixelRatio: 1,
                    mimeType: 'image/png',
                }),
                scaleX: 1,
                scaleY: 1,
            }
        }, [background, template])

        const setSafeZoneImage = () => {
            const image = drawSafeZone()
            if (image) {
                setSafeZone(image)
            }
        }

        /**
         * Handler drag start event
         * @param {DragEvent} e - Drag event
         */
        const handlerDragStart = (_e) => {
            setSafeZoneImage()
        }

        /**
         * Handler add template shape
         * @param {ClickEvent} e - Click event
         */
        const handlerClickTemplateShape = (_e) => {
            setSafeZoneImage()
        }

        return (
            <div
                ref={ref}
                draggable
                onDragStart={handlerDragStart}
                onClick={handlerClickTemplateShape}
                className={clsx('ShapeItem', className)}
                title={description}
                {...rest}
            >
                {typeof onEdit === 'function' && (
                    <button
                        type="button"
                        className="ShapeItemEditButton"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            onEdit()
                        }}
                        aria-label="Edit shape"
                    >
                        Edit
                    </button>
                )}
                <Stage
                    width={canvasSize}
                    height={canvasSize}
                    className={clsx('ShapeItemStage', isTemplateShape ? 'ShapeItemTemplate' : 'ShapeItemBasic')}
                >
                    <Layer listening={false}>
                        <Shape ref={shapeRef} {...props} />
                    </Layer>
                </Stage>
                <ShapeItemName style={{width: canvasSize}} title={name}>
                    {name}
                </ShapeItemName>
            </div>
        )
    }),
)

const ShapeItemName = forwardRef(({children, className, ...rest}, ref) => {
    return (
        <h5 ref={ref} className={clsx('ShapeItemName', className)} {...rest}>
            {children}
        </h5>
    )
})

export default ShapeItem
